import {Card} from "primereact/card";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {TabPanel, TabView} from "primereact/tabview";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared";
import {roundToDecimals} from "@reva/shared/math";
import {MimeType} from "@reva/shared/mime-type";
import {formatTypedValue} from "@reva/shared/number-format";
import {SEGMENT} from "@reva/shared/reva-spec";
import {groundValueLabel, withGroundValueUnit} from "@reva/shared/standard-ground-value";

import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import {TabHeaderWithTooltip} from "../../component/TabHeaderWithTooltip";
import ValueLendingValueInfo from "../../component/ValueLendingValueInfo";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {selectStandardGroundValues, selectStandardGroundValuesForPdf} from "../../redux/locationInfoSelectors";
import {selectStandardGroundValueError, selectStandardGroundValueLoading} from "../../redux/locationInfoSlice";
import {
    selectAssociatedOutputParamOutputCurried,
    selectVisibleObjectParametersCurried,
} from "../../redux/objectParametersSelectors";
import {selectSegment, updateParamsForParameterType} from "../../redux/objectParametersSlice";
import {
    clearedComments,
    updateMultipleParamsAndValidateAndSubmitFormThunk,
    validateAndSubmitThunk,
} from "../../redux/objectParametersThunk";
import {selectReadOnly} from "../../redux/persistenceSlice";
import {persistMultipleDocumentProperties} from "../../redux/persistenceThunk";
import {selectStandardGroundValueSpecModel} from "../../redux/specSelectors";
import {selectSpec} from "../../redux/specSlice";
import {selectCombinedIndicationData} from "../../redux/valueIndicationSlice";
import {groundValueServiceForWeblink} from "../../util/groundValueServices";
import {aggregateIndicationTableData} from "../../util/indicationTableUtil";
import {rowClassName} from "../value/IndicationTable";
import ErrorMessageContainer from "./ErrorMessageContainer";
import {CollapsableSection, ObjectInputsBlock, SectionTitle} from "./ObjectInputs";
import PersistenceFinalMessage from "./PersistenceFinalMessage";
import SnapshotMessage from "./SnapshotMessage";

const APPRECIATIONS_FOR_SECTION = {tab: "plot", section: "bewertungsgrundstueck"};

/**
 * @returns {React.JSX.Element}
 */
function TabObjectGroundvalue() {
    const {t, i18n, language} = useRevaTranslation();
    const groundvalues = useSelector(selectStandardGroundValues);
    const groundvalueSpec = useSelector(selectStandardGroundValueSpecModel);
    const isGroundValueLoading = useSelector(selectStandardGroundValueLoading);
    const combinedIndicationData = useSelector(selectCombinedIndicationData);
    const segment = useSelector(selectSegment);
    const groundValueEndpoint = segment === SEGMENT.GRST_K ? "COMPARATIVE_VALUE" : "INCOME_VALUE";
    const indicationData = combinedIndicationData?.[groundValueEndpoint] ?? null;
    const revaSpec = useSelector(selectSpec);
    //const standard_ground_value = useSelector((state) => selectSingleParam(state,"standard_ground_value",'PLOT'))
    const standard_ground_value = useSelector(selectAssociatedOutputParamOutputCurried("standard_ground_value"));
    const standard_ground_value_valuation_date = useSelector(
        selectAssociatedOutputParamOutputCurried("standard_ground_value_valuation_date"),
    );
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dispatch = useDispatch();
    useEffect(() => {
        if (!standard_ground_value) {
            // console.info("Kein BRW")
            return;
        }
        if (!standard_ground_value_valuation_date) {
            // console.info("Kein BRW-Datum")
            return;
        }
        if (!groundvalues) {
            // console.info("Keine BRW-Daten")
            return;
        }
        const initialIndex =
            groundvalues.findIndex(
                (g) =>
                    roundToDecimals(standard_ground_value, 1) === roundToDecimals(g.bodenrichtwert, 1) &&
                    standard_ground_value_valuation_date === g.stichtag,
            ) || -1;
        setHighlightedIndex(initialIndex);
    }, [standard_ground_value, standard_ground_value_valuation_date, groundvalues, indicationData]);

    function GroundValueOutputObjectSpecificValues() {
        const toastRef = useRef(null);
        const filteredRevaSpec = {
            ...revaSpec,
            pages: {[groundValueEndpoint]: {...revaSpec.pages[groundValueEndpoint], rows: relevantRows}},
        };

        const renderedRows = indicationData
            ? aggregateIndicationTableData(language, filteredRevaSpec, groundValueEndpoint, indicationData)
            : [];

        if (renderedRows.length === 0) {
            return <div className="mt-4">{t("object.groundvalue.objectspecific.indicationNotAvailable")}</div>;
        }

        const onValueCopy = async (copiedValue) => {
            try {
                await navigator.clipboard.writeText(copiedValue);
                toastRef.current.show({
                    severity: "info",
                    summary: "Info",
                    detail: t("value.valueCopiedToast"),
                });
            } catch (e) {
                console.warn("Could not copy value to clipboard", e);
            }
        };

        return (
            <>
                <SectionTitle
                    title={t("object.groundvalue.objectspecific.title")}
                    collapseKey="groundvalue_output_objectspecific"
                />
                <CollapsableSection collapseKey="groundvalue_output_objectspecific">
                    <DataTable
                        size={"small"}
                        value={renderedRows}
                        stripedRows
                        showHeaders={false}
                        rowClassName={rowClassName}
                        tableClassName="indication-table"
                    >
                        <Column field={"title"} />
                        <Column
                            field={"factor.value"}
                            className={"p-align-right"}
                            body={(data) => <SpecificValue data={data?.factor} onClick={onValueCopy} />}
                        />
                    </DataTable>
                    <Toast ref={toastRef} position="top-center" className="text-left" />
                </CollapsableSection>
            </>
        );
    }

    function SpecificValue({data, onClick}) {
        const valueRef = useRef(null);
        const [mounted, setMounted] = useState(false);

        // Force re-rendering if the component after mounting, so the tooltip can be applied correctly
        useEffect(() => {
            if (valueRef.current && !mounted) {
                setMounted(true);
            }
        }, [mounted]);

        if (data?.originalValue == null) return data.value;

        return (
            <>
                <Tooltip target={valueRef.current} className="custom-tooltip">
                    <div>{t("value.nonRoundedValue") + data.originalValue}</div>
                    <i>{t("value.actions.copyToClipboard")}</i>
                </Tooltip>
                <span ref={valueRef} data-pr-position="top" onClick={() => onClick(data.originalValue)}>
                    {data.value}
                </span>
            </>
        );
    }

    /**
     * @returns {React.JSX.Element}
     */
    function GroundValueOutputStandardGroundValueValues() {
        const {t, language} = useRevaTranslation();
        const rows = useSelector((state) => selectStandardGroundValuesForPdf(state, language));
        return (
            <>
                <SectionTitle
                    title={t("object.groundvalue.standard_ground_value.title")}
                    collapseKey="groundvalue_output_standard_ground_value"
                />
                <CollapsableSection collapseKey="groundvalue_output_standard_ground_value">
                    <DataTable
                        value={rows?.data ?? []}
                        stripedRows
                        showHeaders={false}
                        emptyMessage={t("value.emptyMessage")}
                        tableClassName="indication-table"
                        size="small"
                    >
                        <Column field={"label"} />
                        <Column field={"value"} />
                    </DataTable>
                </CollapsableSection>
            </>
        );
    }

    function GroundValueInputForm() {
        const {t, language} = useRevaTranslation();
        const spec = useSelector(selectVisibleObjectParametersCurried("object/groundvalue", "all"));
        // Nur für den ersten Block "default" statt "all", damit dependsOn="meta_plot_add"-Felder greifen.
        const specWithDependencies = useSelector(selectVisibleObjectParametersCurried("object/groundvalue", "default"));
        if (!spec?.length || !spec?.[0]?.blocks || spec?.[0]?.blocks.length < 3) {
            console.warn("GroundvalueInputForm: spec has an unexpected format");
            return null;
        }

        const blocks = spec?.[0]?.blocks;
        const baseBlock = specWithDependencies?.[0]?.blocks?.[0] ?? blocks[0];
        const page = "object/groundvalue";
        return (
            <div>
                <SectionTitle
                    title={selectByLanguage(language, spec?.[0].titleDe, spec?.[0].titleEn)}
                    firstSection={true}
                    appreciationTab={APPRECIATIONS_FOR_SECTION?.tab}
                    appreciationSection={APPRECIATIONS_FOR_SECTION?.section}
                    collapseKey="groundvalue_grundstueck"
                />
                <CollapsableSection collapseKey="groundvalue_grundstueck">
                    <ObjectInputsBlock block={baseBlock} page={page} />
                </CollapsableSection>
                <SectionTitle title={t("object.groundvalue.input.subtitle")} collapseKey="groundvalue_bodenrichtwert" />
                <CollapsableSection collapseKey="groundvalue_bodenrichtwert">
                    <ObjectInputsBlock block={blocks[1]} page={page} />
                    <ObjectInputsBlock block={blocks[2]} page={page} />
                </CollapsableSection>
            </div>
        );
    }

    function GroundValueCards({groundvalues = [], onSelect = () => {}}) {
        const standardGroundValueError = useSelector(selectStandardGroundValueError);

        if (standardGroundValueError) {
            return <ErrorIndicator titleI18nKey="object.groundvalue.error" errorObject={standardGroundValueError} />;
        }

        if (groundvalues.length === 0) {
            return <div>{t("value.emptyMessage")}</div>;
        }

        return groundvalues
            .map((item, index) => ({...item, index: index}))
            .sort((a, b) => {
                const highlightedDelta = b.highlighted - a.highlighted;
                if (highlightedDelta !== 0) {
                    return highlightedDelta;
                }
                return -1;
            })
            .map((item, index) => (
                <GroundValueCardContent
                    groundvalue={item.groundvalue}
                    index={item.index}
                    highlighted={item.highlighted}
                    key={`brw${index}`}
                    onSelect={onSelect}
                />
            ));
    }

    function GroundValueCardContent({groundvalue, index, onSelect, highlighted}) {
        const readOnly = useSelector(selectReadOnly);
        const toastRef = useRef(null);

        const stichtag = new Date(groundvalue.stichtag).getFullYear();
        const selectedCategory = groundvalueSpec["nutzungsart"].categories.find(
            (c) => c.categoryNumber === groundvalue.nutzungsart,
        );
        const label_header = (language === "de" ? selectedCategory.titleDe : selectedCategory.titleEn).replace(
            ".",
            ` - ${stichtag}`,
        );
        const classNames = highlighted
            ? "GroundValueCard hover:bg-surface-300 dark:hover:bg-surface-500 highlighted"
            : "GroundValueCard hover:bg-surface-300 dark:hover:bg-surface-500";
        const omittedGroundValueKeys = [
            "url",
            "x31467",
            "y31467",
            "geschosszahlMin",
            "geschosszahlMax",
            "vonwann",
            "flaeche_m2",
        ];
        const externalLinks = ["weblink", "weblinkZentriert", "linkUmrechnungstabelle", "bestelllink"];
        const sort_order = [
            "bezGutachterauschusses",
            "wnum",
            "stichtag",
            "bodenrichtwert",
            "entwicklungszustand",
            "sanierungUndEntwicklungszusatz",
            "abgabenrechtlicherZustand",
            "nutzungsart",
            "ergNutzungsart",
            "geschosszahl",
            "wertrelevanteGeschossflaechenzahl",
            "grundflaechenzahl",
            "grundstueckstiefe",
            "grundstuecksbreite",
            "bauweise",
            "flaeche",
            "linkUmrechnungstabelle",
            "weblink",
            "weblinkZentriert",
            "bestelllink",
            "flaeche_m2",
            "url",
            "x31467",
            "y31467",
            "vonwann",
        ];
        const vonwannYear = new Date(groundvalue.vonwann).getFullYear();
        const service = groundValueServiceForWeblink(groundvalue.weblink);
        const serviceTitle = service?.title;
        const serviceLicense = service?.license;
        let sourcelabel = null;
        const groundvalueForDocProperty = {};
        const elementData = Object.keys(groundvalueSpec)
            .filter((k) => !omittedGroundValueKeys.includes(k))
            .sort((a, b) => sort_order.indexOf(a) - sort_order.indexOf(b))
            .map((key) => {
                const translationKey = `object.groundvalue.output.${key}`;
                const value = groundvalue[key];
                if (!i18n.exists(translationKey))
                    return {
                        key,
                        label: groundValueLabel(key, groundvalueSpec[key], language),
                        value,
                    };
                return {key, label: t(translationKey), value};
            })
            .map(({key, label, value}) => {
                if (key === "bezGutachterauschusses" && !value) {
                    sourcelabel = `${serviceTitle} ${serviceLicense} ${vonwannYear}`;
                    return {
                        key,
                        label,
                        value: (
                            <div>
                                {serviceTitle}
                                <br />
                                {serviceLicense} {vonwannYear}
                            </div>
                        ),
                    };
                }

                if (!value || value === -1 || value === 99) {
                    return {key, label, value: "-"};
                }

                if (key === "bodenrichtwert") {
                    const guiType = {
                        alwaysShowSign: false,
                        baseType: "NUMBER",
                        decimalPlacesMax: 2,
                        decimalPlacesMin: 2,
                        grouped: true,
                        unitDE: "€/m²",
                        unitEN: "€/m²",
                    };
                    const formatted = formatTypedValue(language, value, guiType);
                    return {key, label: <strong>{label}</strong>, value: <strong>{formatted}</strong>};
                }

                if (key === "stichtag") {
                    const formatted = new Intl.DateTimeFormat(language, {dateStyle: "medium"}).format(new Date(value));
                    return {key, label: <strong>{label}</strong>, value: <strong>{formatted}</strong>};
                }

                if (key === "wertrelevanteGeschossflaechenzahl") {
                    return {key, label: <strong>{label}</strong>, value: <strong>{value}</strong>};
                }

                if (key === "bezGutachterauschusses") {
                    sourcelabel = `${serviceTitle} © ${value} ${vonwannYear} ${serviceLicense}`;
                    return {
                        key,
                        label,
                        value: (
                            <div>
                                {serviceTitle}
                                <br />© {value} {vonwannYear}
                                <br />
                                {serviceLicense}
                            </div>
                        ),
                    };
                }

                if (externalLinks.includes(key)) {
                    // #19313: Nicht jeder Wert der Gutachterausschüsse ist eine URL. Sachsen-Anhalt liefert
                    // im Feld der Umrechnungstabelle deren amtliche Nummer (z.B. "150320058"). Als href
                    // gerendert wäre das ein relativer Link ins Nichts — also nur echte URLs verlinken und
                    // alles andere als Text zeigen. Aufgeräumt wird zentral in immobrain-db
                    // (BodenrichtwertLinkNormalizer); das hier ist die Absicherung im Frontend.
                    // Auch der Nicht-Link-Fall bleibt bewusst JSX, damit er wie bisher aus
                    // groundvalueForDocProperty herausgefiltert wird (siehe Filter auf value.type unten).
                    const isWeblink = typeof value === "string" && /^https?:\/\//i.test(value);
                    return {
                        key,
                        label,
                        value: isWeblink ? (
                            <a href={value} target="_blank" rel="noreferrer">
                                {t("object.groundvalue.output.linkPrefix")} {label}
                                <i className="pi pi-external-link m-1"></i>
                            </a>
                        ) : (
                            <span>{value}</span>
                        ),
                    };
                }

                if (groundvalueSpec[key].type !== "category") return {key, label, value};

                const category = groundvalueSpec[key].categories.find((c) => c.categoryNumber === value);
                if (!category) {
                    console.warn(`Could not resolve category for value ${value}`, groundvalueSpec[key].categories);
                    return {key, label, value: "-"};
                }

                return {key, label, value: language === "de" ? category.titleDe : category.titleEn};
            })
            .map(({key, label, value}) => {
                // #19437: Die Einheiten (m² für die Grundstücksgröße, m für Tiefe und Breite) stehen in der
                // AVM-Spec, wurden aber nie gerendert. Der Rohwert bleibt als docValue erhalten, damit die
                // persistierten DOC_PROPERTY-Werte weiterhin ohne Einheit in der DB landen.
                if (typeof value !== "string" && typeof value !== "number") return {key, label, value, docValue: value};
                return {
                    key,
                    label,
                    value: withGroundValueUnit(key, value, groundvalueSpec[key], language),
                    docValue: value,
                };
            });
        elementData
            .filter(({docValue}) => docValue !== "-" && !docValue.type)
            .forEach(({key, docValue}) => {
                groundvalueForDocProperty[key] = docValue;
            });

        const elements = elementData.map(({key, label, value}) => (
            <div className="flex flex-row" key={`brw${index}_${key}`}>
                <div className="w-full">{label}</div>
                <div className="w-full">{value}</div>
            </div>
        ));

        const getValue = (value, key) => {
            if (key === "bezGutachterauschusses")
                return groundvalue[key]
                    ? `${serviceTitle}\n© ${groundvalue[key]} ${vonwannYear}\n${serviceLicense}`
                    : `${serviceTitle}\n${serviceLicense} ${vonwannYear}`;
            const raw = value?.props?.children ?? value;
            if (key === "bodenrichtwert") return String(raw).replace(/,00(\s)/, "$1");
            if (key === "wertrelevanteGeschossflaechenzahl") return String(raw).replace(".", ",");
            if (typeof raw === "string") return raw.replace(/\.$/, "");
            return raw;
        };

        //output is meant for copying into word that works with obsolete html attributes better than with style
        const buildHtml = (filteredRows) => {
            const tdBorderStyle =
                "border-top:1px solid black;border-bottom:1px solid black;padding-top:2pt;padding-bottom:2pt;";
            const pStyle =
                "font-family:Arial;font-size:10pt;mso-para-margin-left:5.4;margin-left:5.4pt;margin-bottom:1pt;mso-para-margin-bottom:1;";
            const tdLabel = (text) =>
                `<td valign="top" width="37%" style="background-color:#F2F2F2;${tdBorderStyle}"><p style="${pStyle}">${text}</p></td>`;
            const tdValue = (text) =>
                `<td style="${tdBorderStyle}"><p style="${pStyle}">${String(text).replace(/\n/g, "<br>")}</p></td>`;
            const bodyRows = filteredRows
                .map(
                    ({key, label, value}) =>
                        `<tr>${tdLabel(label?.props?.children ?? label)}${tdValue(getValue(value, key))}</tr>`,
                )
                .join("");
            return `<table width="643pt" cellspacing="0" style="width:17cm;border-collapse:collapse">
                <tbody>${bodyRows}</tbody>
            </table>`;
        };

        const copyTable = async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const filteredRows = elementData.filter(({key, value}) => value !== "-" && !externalLinks.includes(key));
            const html = buildHtml(filteredRows);
            const plainText = filteredRows.reduce(
                (acc, {key, label, value}) => `${acc}${label?.props?.children ?? label}:\t${getValue(value, key)}\n`,
                "",
            );

            try {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        [MimeType.TEXT_HTML]: new Blob([html], {type: MimeType.TEXT_HTML}),
                        [MimeType.TEXT_PLAIN]: new Blob([plainText], {type: MimeType.TEXT_PLAIN}),
                    }),
                ]);
                toastRef.current.show({severity: "info", summary: "Info", detail: t("general.dataCopied")});
            } catch (e) {
                console.warn("Could not copy table content to clipboard", e);
            }
        };

        const cardTitle = (
            <div className="flex flex-row items-center">
                <span className="flex-1">{label_header}</span>
                <i
                    className="pi pi-copy tooltip-icon text-primary cursor-pointer text-2xl"
                    data-pr-position="top"
                    data-pr-tooltip={t("general.copyTooltip")}
                    onClick={copyTable}
                />
                <Tooltip target={".tooltip-icon"} className="custom-tooltip" />
            </div>
        );

        //Bei schreibgeschützten Gutachten (Snapshot/Final) wollen wir keine Änderungen des Bodenrichtwerts mehr zulassen
        if (readOnly)
            return (
                <Card className={classNames} key={`brw${index}`} title={cardTitle}>
                    {elements}
                    <Toast ref={toastRef} position="top-center" />
                </Card>
            );

        const onClickHandler = () => {
            // Die drei PLOT-Parameter in EINER Request: als Einzel-Updates liefen sie nebenläufig,
            // ihre Antworten konnten sich überholen und den Store auf einen älteren Vollstand
            // zurückdrehen — und die Indikation lief dreimal statt einmal.
            const plotParams = {
                standard_ground_value: groundvalue.bodenrichtwert,
                standard_ground_value_valuation_date: groundvalue.stichtag,
                standard_ground_value_floor_area_ratio: groundvalue.wertrelevanteGeschossflaechenzahl,
            };
            dispatch(
                updateMultipleParamsAndValidateAndSubmitFormThunk("PLOT", plotParams, clearedComments(plotParams)),
            );

            const docProperties = {
                standard_ground_value_abgabenrechtlicher_zustand: groundvalueForDocProperty.abgabenrechtlicherZustand,
                standard_ground_value_bauweise: groundvalueForDocProperty.bauweise,
                standard_ground_value_bez_gutachterauschusses: groundvalueForDocProperty.bezGutachterauschusses,
                standard_ground_value_entwicklungszustand: groundvalueForDocProperty.entwicklungszustand,
                standard_ground_value_erg_nutzungsart: groundvalueForDocProperty.ergNutzungsart,
                standard_ground_value_flaeche: groundvalueForDocProperty.flaeche,
                standard_ground_value_geschosszahl: groundvalueForDocProperty.geschosszahl,
                standard_ground_value_grundflaechenzahl: groundvalueForDocProperty.grundflaechenzahl,
                standard_ground_value_grundstuecksbreite: groundvalueForDocProperty.grundstuecksbreite,
                standard_ground_value_grundstueckstiefe: groundvalueForDocProperty.grundstueckstiefe,
                standard_ground_value_link_umrechnungstabelle: groundvalueForDocProperty.linkUmrechnungstabelle,
                standard_ground_value_number: groundvalueForDocProperty.wnum,
                standard_ground_value_nutzungsart: groundvalueForDocProperty.nutzungsart,
                standard_ground_value_sanierung_und_entwicklungszusatz:
                    groundvalueForDocProperty.sanierungUndEntwicklungszusatz,
                standard_ground_value_source: sourcelabel,
            };
            dispatch(updateParamsForParameterType({parameterType: "DOC_PROPERTY", params: docProperties}));
            dispatch(persistMultipleDocumentProperties(docProperties));
            dispatch(validateAndSubmitThunk());
            onSelect(index);
        };

        return (
            <Card className={classNames} key={`brw${index}`} title={cardTitle} onClick={() => onClickHandler()}>
                {elements}
                <Toast ref={toastRef} position="top-center" />
            </Card>
        );
    }

    if (isGroundValueLoading) {
        return <LoadingIndicator loading="true" />;
    }

    const bodenrichtwert = standard_ground_value;
    const stichtag = standard_ground_value_valuation_date;
    const relevantRows = revaSpec.pages[groundValueEndpoint].rows.filter((r) => r.group === "bodenwert");

    const highlightedGroundValues = (groundvalues ?? []).map((g, i) => {
        if (highlightedIndex > -1) return {groundvalue: g, highlighted: highlightedIndex === i};
        const highlighted = bodenrichtwert === g.bodenrichtwert && stichtag === g.stichtag;
        return {groundvalue: g, highlighted: highlighted};
    });

    return (
        <div id="groundValuePanelTitle">
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <AppreciationTabInputText page="object" tab="plot" />
            <ValueLendingValueInfo />
            <ErrorMessageContainer />

            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                    <GroundValueInputForm />
                </div>

                <div className="col-span-6">
                    <TabView className="groundValueTabView" activeIndex={0}>
                        <TabPanel
                            key="groundvalueoutput"
                            header={<TabHeaderWithTooltip i18nPrefix="object.groundvalue.preview" />}
                        >
                            <GroundValueOutputObjectSpecificValues />
                            <GroundValueOutputStandardGroundValueValues />
                        </TabPanel>
                        <TabPanel
                            key="groundvaluecards"
                            header={<span className="tabViewTitle">{t("object.groundvalue.output.title")}</span>}
                        >
                            <GroundValueCards
                                key="groundvaluecards"
                                groundvalues={highlightedGroundValues}
                                onSelect={(i) => {
                                    setHighlightedIndex(i);
                                    dispatch(() =>
                                        document.getElementById("root").scrollIntoView({behavior: "smooth"}),
                                    );
                                }}
                            />
                        </TabPanel>
                    </TabView>
                </div>
            </div>
        </div>
    );
}

export default TabObjectGroundvalue;
